import { useState, useMemo, useCallback } from 'react';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import { useGetBenefitContent, useGetCancellationReasons, useGetCancellationOffer, useApplyCancellationOffer, useCanCancelSubscription, useCancelSubscription, useSubmitCancellationFeedback, useGetChangePlanData, useLogBenefitResponse } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type { CancellationReason } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { cn } from '@/utils/styles';
import { logSMPMutationError } from '@/utils/sentryLogger';
import { isSubscriptionActiveState, isSubscriptionPaused, formatProductName, getProductImageForSMP, getProductGradientClass, isComponentizationEligible } from './utils/smpHelpers';
import { SMP_PATH, SMP_PAUSE_SUBSCRIPTION_PATH, SMP_ADD_REMOVE_PRODUCTS_PATH } from '@/constants/paths';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { RadioOptionCard, SMPDialogContent, SMPMutationErrorDialog, ShimmerImage } from './components';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';

type Step = 'video-retention' | 'reason-selection' | 'offer' | 'offer-detail' | 'offer-claimed' | 'cancelled' | 'thank-you';

export const SMPCancelSubscriptionPage = () => {
  const { smpNavigate, smpGoBack } = useSMPNavigate();
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const { subscriptionId, subscriptionDetail, hasOrderInProgress, hasPersonalizedSupplementRecommendations, isEligibleForSupplements, showMaintenance, isSessionReady, isLoading: isSMPLoading, isError: isSMPError, queryParams } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const [step, setStep] = useState<Step>('video-retention');
  const [selectedReason, setSelectedReason] = useState<CancellationReason | null>(null);
  const [otherReasonComment, setOtherReasonComment] = useState('');
  const [showCommentError, setShowCommentError] = useState(false);
  const [surveyValue, setSurveyValue] = useState('');
  const [cxDialogOpen, setCxDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isOipExpanded, setIsOipExpanded] = useState(false);

  const cancelMutation = useCancelSubscription();
  const feedbackMutation = useSubmitCancellationFeedback();
  const applyOfferMutation = useApplyCancellationOffer();
  const logBenefitResponseMutation = useLogBenefitResponse();
  const { data: benefitContent, isLoading: isBenefitLoading, isError: isBenefitError } = useGetBenefitContent(queryParams, { enabled: isSessionReady && !!subscriptionId });
  const { data: cancellationReasons, isLoading: isReasonsLoading, isError: isReasonsError } = useGetCancellationReasons(queryParams, { enabled: isSessionReady && !!subscriptionId });
  const { data: cancellationOffer, isLoading: isOfferLoading } = useGetCancellationOffer(
    { ...queryParams, cancellationFlowId: selectedReason?.id ?? 0 },
    { enabled: isSessionReady && !!subscriptionId && !!selectedReason && (step === 'offer' || step === 'offer-detail') },
  );
  const { data: cancellableData, isLoading: isCancellableLoading, isError: isCancellableError } = useCanCancelSubscription(subscriptionId, { enabled: isSessionReady && !!subscriptionId });
  const { data: changePlanData, isLoading: isChangePlanLoading, isError: isChangePlanError } = useGetChangePlanData(subscriptionId, { enabled: isSessionReady && !!subscriptionId });

  const cancelReasons = useMemo(() => {
    if (!cancellationReasons) return [];
    return cancellationReasons;
  }, [cancellationReasons]);

  const products = useMemo(() => {
    if (!subscriptionDetail) return [];

    const components = changePlanData?.current_components ?? [];
    const lineItems = subscriptionDetail.line_items ?? [];

    if (components.length > 0) {
      return components.map((comp) => {
        const rawTitle = comp.title || '';
        return {
          title: formatProductName(rawTitle),
          rawTitle,
          image: getProductImageForSMP(rawTitle) || comp.image || '',
        };
      });
    }

    return lineItems.map((li) => {
      const rawTitle = li.product_name || li.title || '';
      return {
        title: formatProductName(rawTitle),
        rawTitle,
        image: getProductImageForSMP(rawTitle) || li.image || '',
      };
    });
  }, [subscriptionDetail, changePlanData]);

  const logBenefitAction = useCallback(
    (state: 'ACCEPTED' | 'REJECTED') => {
      if (!benefitContent?.id) return;
      logBenefitResponseMutation.mutate({
        subscriptionId,
        customerId: queryParams.customerId,
        benefit_content_id: benefitContent.id,
        state,
      });
    },
    [benefitContent?.id, subscriptionId, queryParams.customerId, logBenefitResponseMutation],
  );

  const goBack = useCallback(() => smpGoBack(), [smpGoBack]);
  const returnToAccount = useCallback(
    () => smpNavigate(SMP_PATH, { replace: true }),
    [smpNavigate],
  );
  const isOtherReason = selectedReason?.reason?.toLowerCase() === 'other reason';
  const isLoading = isSMPLoading || isBenefitLoading || isReasonsLoading || isCancellableLoading || isChangePlanLoading;

  if (!initialized && !isLoading && subscriptionDetail) {
    setInitialized(true);
  }

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </section>
    );
  }

  if (isSMPError || isBenefitError || isReasonsError || isCancellableError || isChangePlanError || !subscriptionDetail) return <ErrorLoadingPage variant="light" errorType="error" hideBackground />;

  if (!isSubscriptionActiveState(subscriptionDetail.subscription_status) && step !== 'cancelled' && step !== 'thank-you') {
    smpNavigate(SMP_PATH, { replace: true });
    return null;
  }

  // Componentized subscriptions don't support Add/Remove Products — hide
  // "Customize my plan" on the cancel retention offer step.
  const isComponentized =
    isComponentizationEnabled &&
    isComponentizationEligible(
      subscriptionDetail.componentization,
      subscriptionDetail.line_items,
      eligibleSkus,
    );

  const handleConfirmCancel = () => {
    if (!cancellableData?.canCancel) { setCxDialogOpen(true); return; }
    cancelMutation.mutate(
      {
        subscriptionId,
        customerId: queryParams.customerId,
        cancelReason: selectedReason?.reason ?? 'Not specified',
        cancellationFlowId: selectedReason?.id ?? 0,
        ...(otherReasonComment.trim() ? { comment: otherReasonComment.trim() } : {}),
        ...(cancellationOffer?.data?.cancellationOfferId ? { cancellation_offer_id: cancellationOffer.data.cancellationOfferId } : {}),
      },
      {
        onSuccess: () => setStep('cancelled'),
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('cancelSubscription', error, {
            screen: 'SMPCancelSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleSubmitFeedback = () => {
    feedbackMutation.mutate(
      { subscriptionId, feedback: surveyValue, source: 'smp-cancellation' },
      {
        onSuccess: () => setStep('thank-you'),
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('cancellationFeedback', error, {
            screen: 'SMPCancelSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const stepBack: Record<Step, () => void> = {
    'video-retention': goBack,
    'reason-selection': () => setStep('video-retention'),
    'offer': () => setStep('reason-selection'),
    'offer-detail': () => setStep('offer'),
    'offer-claimed': returnToAccount,
    'cancelled': returnToAccount,
    'thank-you': returnToAccount,
  };

  return (
    <section className="page-container flex flex-col gap-12 pb-24" data-testid="smp-cancelPage">
      <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={stepBack[step]} />

      {step === 'video-retention' && benefitContent && (
        <div className="flex flex-col gap-8 items-center text-center">
          <h1 className="typography-h6 text-primary">{benefitContent.title}</h1>
          <div
            className="w-full max-w-xl [&_video]:rounded-2xl [&_video]:w-full [&_p]:typography-body [&_p]:text-secondary"
            dangerouslySetInnerHTML={{ __html: benefitContent.body }}
          />
          {(benefitContent.keepSubscriptionCTA || benefitContent.cancelSubscriptionCTA) && (
            <div className="flex flex-col gap-4 w-full max-w-sm">
              {benefitContent.keepSubscriptionCTA && (
                <Button onClick={() => { logBenefitAction('ACCEPTED'); goBack(); }}>{benefitContent.keepSubscriptionCTA}</Button>
              )}
              {benefitContent.cancelSubscriptionCTA && (
                <Button variant="secondary" onClick={() => { logBenefitAction('REJECTED'); setStep('reason-selection'); }}>{benefitContent.cancelSubscriptionCTA}</Button>
              )}
            </div>
          )}
        </div>
      )}

      {step === 'reason-selection' && (
        <div className="flex flex-col gap-8">
          <h2 className="typography-headline2 text-primary max-w-[600px]">Please tell us the main reason why you want to cancel your subscription.</h2>
          <div className="flex flex-col gap-3 w-full max-w-sm">
            {cancelReasons.map((r) => (
              <RadioOptionCard key={r.id} label={r.reason} selected={selectedReason?.id === r.id} onSelect={() => { setSelectedReason(r); if (r.reason?.toLowerCase() !== 'other reason') { setOtherReasonComment(''); setShowCommentError(false); } }} testId={`smp-cancel-reason-${r.id}`} />
            ))}
          </div>
          {isOtherReason && (
            <textarea
              className={`w-full max-w-sm rounded-xl border p-4 typography-body text-primary resize-none focus:outline-none focus:ring-2 ${showCommentError && !otherReasonComment.trim() ? 'border-error focus:ring-error/50' : 'border-stroke-secondary-subtle focus:ring-success/50'}`}
              rows={4}
              value={otherReasonComment}
              onChange={(e) => { setOtherReasonComment(e.target.value); if (showCommentError) setShowCommentError(false); }}
              placeholder="Please tell us more..."
            />
          )}
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button onClick={() => { if (isOtherReason && !otherReasonComment.trim()) { setShowCommentError(true); return; } if (selectedReason) setStep('offer'); }} disabled={!selectedReason}>Continue</Button>
            <Button variant="secondary" onClick={goBack}>Keep subscription active</Button>
          </div>
        </div>
      )}

      {step === 'offer' && (
        <div className="flex flex-col gap-8">
          {isOfferLoading ? (
            <>
              <Skeleton className="h-24 w-full max-w-xl rounded-xl" />
              <Skeleton className="h-12 w-full max-w-sm rounded-full" />
            </>
          ) : (
            <>
              {selectedReason?.actionMessage && (
                <div
                  className="typography-body text-primary max-w-xl [&_img]:rounded-xl [&_img]:my-4"
                  dangerouslySetInnerHTML={{ __html: selectedReason.actionMessage }}
                />
              )}
              <div className="flex flex-col gap-4 w-full max-w-sm">
                {!isSubscriptionPaused(subscriptionDetail.subscription_status) && (
                  <Button variant="secondary" onClick={() => smpNavigate(SMP_PAUSE_SUBSCRIPTION_PATH, { state: { cancellationFlowId: selectedReason?.id } })} disabled={applyOfferMutation.isPending}>Pause subscription</Button>
                )}
                {isEligibleForSupplements && hasPersonalizedSupplementRecommendations && !isComponentized && (
                  <Button variant="secondary" onClick={() => smpNavigate(SMP_ADD_REMOVE_PRODUCTS_PATH, { state: { cancellationFlowId: selectedReason?.id } })} disabled={applyOfferMutation.isPending}>Customize my plan</Button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (cancellationOffer?.data?.cancellationOfferId) {
                      setStep('offer-detail');
                    } else {
                      handleConfirmCancel();
                    }
                  }}
                  disabled={cancelMutation.isPending}
                  className="typography-body text-error text-center py-3 bg-white rounded-[20px]"
                >
                  {cancelMutation.isPending ? 'Cancelling...' : 'Cancel subscription now'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'offer-detail' && cancellationOffer?.data && (
        <div className="flex flex-col gap-8 max-w-md">
          <h1 className="typography-h2 text-primary">{cancellationOffer.data.offerDescription}</h1>
          <p className="typography-body text-primary">
            Keep your subscription active and this discount will automatically be applied to your upcoming orders.
          </p>
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={() => {
                if (!selectedReason || !cancellationOffer.data?.cancellationOfferId) return;
                applyOfferMutation.mutate(
                  {
                    subscriptionId,
                    customerId: queryParams.customerId,
                    cancellation_flow_id: selectedReason.id,
                    cancellation_offer_id: cancellationOffer.data.cancellationOfferId,
                  },
                  {
                    onSuccess: () => setStep('offer-claimed'),
                    onError: (error) => {
                      setShowErrorDialog(true);
                      logSMPMutationError('claimRetentionOffer', error, {
                        screen: 'SMPCancelSubscriptionPage',
                        subscriptionId,
                      });
                    },
                  },
                );
              }}
              disabled={applyOfferMutation.isPending}
            >
              {applyOfferMutation.isPending ? 'Applying...' : 'Redeem Now'}
            </Button>
            <button
              type="button"
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending || applyOfferMutation.isPending}
              className="typography-body text-error text-center py-3 bg-white rounded-[20px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Continue to Cancel Subscription'}
            </button>
          </div>
        </div>
      )}

      {step === 'offer-claimed' && (
        <div className="flex flex-col gap-12">
          <h1 className="typography-d1 text-primary max-w-[554px]">Offer Claimed!</h1>
          <p className="typography-headline3 text-primary max-w-[560px]">Offer applied successfully! Your wellness journey is worth continuing. Thank you for letting us be a part of it.</p>
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button onClick={returnToAccount}>Return to My Account</Button>
          </div>
        </div>
      )}

      {step === 'cancelled' && (
        <div className="flex flex-col gap-8">
          {hasOrderInProgress && (() => {
            const isCollapsible = products.length > 1;
            const visibleProducts = isCollapsible && !isOipExpanded ? products.slice(0, 1) : products;

            return (
              <div className="relative flex flex-col gap-6 rounded-[20px] py-6 px-5 max-w-2xl shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] backdrop-blur-[25px] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FCF2EB] to-[#FCF9E3] rounded-[20px] pointer-events-none" />
                <div className="absolute inset-0 rounded-[20px] pointer-events-none shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]" />
                {visibleProducts.map((product) => (
                  <div key={product.rawTitle} className="relative flex items-center gap-4">
                    {product.image && (
                      <div
                        className={cn(
                          'relative size-[72px] rounded-xl overflow-hidden shrink-0 flex items-center justify-center',
                          getProductGradientClass(product.rawTitle) || 'bg-white',
                        )}
                      >
                        <ShimmerImage src={product.image} alt={product.title} containerClassName="size-[40px]" className="size-full object-contain" />
                      </div>
                    )}
                    <p className="text-primary text-[18px] leading-[1.3]">
                      <span className="font-medium">Order in progress:</span>
                      {'  '}{product.title}
                    </p>
                  </div>
                ))}
                {isCollapsible && (
                  <button
                    type="button"
                    onClick={() => setIsOipExpanded((prev) => !prev)}
                    className="relative flex items-center justify-center gap-1 py-1 typography-body text-primary cursor-pointer"
                  >
                    {isOipExpanded ? 'Show less' : `Show ${products.length - 1} more`}
                    <ChevronRightIcon
                      className={cn(
                        'size-6 transition-transform',
                        isOipExpanded ? '-rotate-90' : 'rotate-90',
                      )}
                    />
                  </button>
                )}
                <p className="relative text-primary text-[16px] leading-[1.3] font-light">
                  You currently have an order in progress.  Any changes you make will not affect this order and it will be delivered as scheduled.
                </p>
              </div>
            );
          })()}
          <p className="typography-body text-primary max-w-xl">Your subscription is now canceled and confirmation will be sent to you via email.</p>
          <p className="typography-body text-primary max-w-xl">You can reactivate your subscription anytime.</p>
          <p className="typography-body text-primary max-w-xl">We value your feedback and would appreciate your input. How can we improve our products and services?</p>
          <textarea className="w-full max-w-xl rounded-xl border border-stroke-secondary-subtle p-4 typography-body text-primary resize-none focus:outline-none focus:ring-2 focus:ring-success/50" rows={6} value={surveyValue} onChange={(e) => setSurveyValue(e.target.value)} placeholder="Input here..." />
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button onClick={handleSubmitFeedback} disabled={!surveyValue.trim() || feedbackMutation.isPending}>{feedbackMutation.isPending ? 'Submitting...' : 'Submit'}</Button>
            <Button variant="secondary" onClick={returnToAccount} disabled={feedbackMutation.isPending}>I don&apos;t have any feedback</Button>
          </div>
        </div>
      )}

      {step === 'thank-you' && (
        <div className="flex flex-col gap-8">
          <h2 className="typography-h2 text-primary">Thank you for your feedback!</h2>
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button onClick={returnToAccount}>Return to My Account</Button>
          </div>
        </div>
      )}

      <Dialog open={cxDialogOpen} onOpenChange={(o) => !o && (setCxDialogOpen(false), goBack())}>
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">Contact Support</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-4">
                <p className="typography-body text-secondary">Your subscription cannot be cancelled online at this time. Please contact our Customer Experience team for assistance.</p>
                <a href="mailto:support@viome.com" className="typography-body text-success font-medium">support@viome.com</a>
              </div>
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="We couldn't process your request. Please try again."
      />
    </section>
  );
};
